import React, { lazy, Suspense, useMemo, useState } from 'react';
import type { TradeBriefingContent, TradeBriefingRelease } from '../../types';
import { portalHostedAssetHref, portalHtmlPreviewHref } from '../../utils/portalAssetUrl';
import {
  briefingAnnotationsToPdf,
  isTradeBriefingHtmlRelease,
  tradeBriefingPublicFileLabel,
  tradeBriefingPublicVersionLabel,
} from '../../utils/tradeBriefingFiles';
import TradeBriefingStructureCards from './TradeBriefingStructureCards';

const PdfViewerModal = lazy(() => import('../pdf/PdfViewerModal'));

type TradeBriefingDeliverablesSectionProps = {
  content: TradeBriefingContent;
  dealId: string;
};

const TradeBriefingDeliverablesSection: React.FC<TradeBriefingDeliverablesSectionProps> = ({
  content,
}) => {
  const [openPdf, setOpenPdf] = useState<TradeBriefingRelease | null>(null);

  const rows = useMemo(() => {
    const models = content.releases.filter(isTradeBriefingHtmlRelease);
    const pdfs = content.releases.filter(release => !isTradeBriefingHtmlRelease(release));
    return [...models, ...pdfs];
  }, [content.releases]);

  const seededAnnotations = useMemo(
    () =>
      openPdf
        ? briefingAnnotationsToPdf(openPdf.annotations, openPdf.id, openPdf.releasedUrl)
        : [],
    [openPdf],
  );

  const structures = content.structures || [];
  if (rows.length === 0 && structures.length === 0) return null;

  return (
    <section className="tb-section" aria-labelledby="tb-files-heading">
      <div className="tb-section-head">
        <div>
          <p className="tb-kicker">Files</p>
          <h2 id="tb-files-heading">Drawings and models</h2>
        </div>
      </div>

      {rows.length > 0 && (
        <div className="tb-table-wrap">
          <table className="tb-table">
            <thead>
              <tr>
                <th scope="col">File</th>
                <th scope="col">Type</th>
                <th scope="col">Version</th>
                <th scope="col">Open</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(release => {
                const isModel = isTradeBriefingHtmlRelease(release);
                const label = tradeBriefingPublicFileLabel(release);
                const versionLabel = tradeBriefingPublicVersionLabel(release);
                const downloadHref = portalHostedAssetHref(release.releasedUrl, {
                  downloadName: release.name,
                });

                return (
                  <tr key={release.id}>
                    <td className="tb-file-name">{label}</td>
                    <td>
                      <span className={`tb-type ${isModel ? 'tb-type--3d' : 'tb-type--pdf'}`}>
                        {isModel ? '3D' : 'PDF'}
                      </span>
                    </td>
                    <td>{versionLabel}</td>
                    <td className="tb-table-actions">
                      {isModel ? (
                        <a
                          href={portalHtmlPreviewHref(release.releasedUrl, { sizeBytes: release.size })}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          View
                        </a>
                      ) : (
                        <>
                          <button type="button" onClick={() => setOpenPdf(release)}>
                            Open
                          </button>
                          <a href={downloadHref} download={release.name}>
                            Download
                          </a>
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <TradeBriefingStructureCards
        structures={structures}
        modelCaption={
          rows.some(isTradeBriefingHtmlRelease)
            ? 'These structures are in the 3D model above.'
            : rows.length
              ? 'These structures belong to the drawings and models above.'
              : 'Structures included in this briefing.'
        }
      />

      {openPdf ? (
        <Suspense fallback={null}>
          <PdfViewerModal
            url={openPdf.releasedUrl}
            title={tradeBriefingPublicFileLabel(openPdf)}
            documentId={openPdf.id}
            leadId={content.leadId || content.dealId}
            leadName={content.clientLabel}
            projectId={content.timberProjectId}
            dealId={content.dealId}
            initialAnnotations={seededAnnotations}
            disableTakeoff
            onClose={() => setOpenPdf(null)}
          />
        </Suspense>
      ) : null}
    </section>
  );
};

export default TradeBriefingDeliverablesSection;
